import { useState, useMemo, useEffect, useRef } from 'react';
import { View, Modal, ScrollView, Pressable, Alert, ActivityIndicator, Platform, KeyboardAvoidingView, Dimensions, StyleSheet, Image, Linking } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import * as FileSystem from '@/lib/fileSystemLegacy';
import { BlurView } from 'expo-blur';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { SelectField, type SelectOption } from '@/components/ui/SelectField';
import { SelectFieldModalProvider } from '@/components/ui/SelectFieldModalContext';
import { ModalHeader } from '@/components/ui/ModalHeader';
import { ThemedText, SubHeading, Caption } from '@/components/ui/Text';
import { Calendar } from '@/components/ui/Calendar';
import type { DocumentType, DocumentMetadata, EntryType } from '@/services/documentVault';
import { prepareDocumentUpload, finalizeDocumentUpload } from '@/services/documentVault';
import { startDocumentScan, extractDocumentFields } from '@/services/documentOcr';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DocumentFormFields } from './DocumentFormFields';
import { useUserStore } from '@/state/userStore';
import { networkMonitor } from '@/services/networkMonitor';
import {
  copyUriToPersistentPendingUpload,
  queueDocumentUpload,
  queueManualDocumentSave,
} from '@/services/documentUploadQueue';
import {
  DOCUMENT_ALLOWED_EXTENSIONS,
  DOCUMENT_ALLOWED_MIME_TYPES,
  MAX_DOCUMENT_SIZE_BYTES,
  getFileExtension,
  hasAllowedMimeOrExtension,
} from '@/services/uploadPolicy';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { logger } from '@/services/logger';

type DocumentUploadModalProps = {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialEntryMode?: EntryMode;
  /**
   * If false, users can only create "manual" documents (no file upload/scan).
   * Used for freemium gating (Document upload is Premium).
   */
  allowUpload?: boolean;
};

const DOCUMENT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'passport', label: 'Passport' },
  { value: 'visa', label: 'Visa' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'health_record', label: 'Health Record' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' },
];

type EntryMode = 'upload' | 'manual';

type SelectedFile = {
  uri: string;
  name: string;
  type: string;
  size: number;
};

type ScanUploadPayload = {
  fileBlob: Blob;
  contentType: string;
  optimized: boolean;
  sourceUri: string;
  preparedAtMs: number;
};

type ScanReviewState = {
  parseStatus?: 'parsed' | 'needs_review' | 'failed';
  parseWarnings?: string[];
  fieldConfidence?: Record<string, number>;
  missingRequiredFields?: string[];
  rawText?: string;
  detectedDocumentType?: string;
  ocrEngine?: string;
  extractionSource?: string;
};

const formatFileSize = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/** Firebase Storage often returns "Missing or insufficient permissions" when rules reject the write. */
const humanizeScanStartError = (error: unknown): string => {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code?: string }).code || '')
      : '';
  const msg = error instanceof Error ? error.message : String(error);
  const combined = `${code} ${msg}`.toLowerCase();

  if (/scan job/i.test(msg)) {
    return msg;
  }
  if (
    combined.includes('permission') ||
    combined.includes('unauthorized') ||
    combined.includes('storage/unauthorized')
  ) {
    return (
      'Could not upload this file for scanning (access was denied). ' +
      'Publish Storage rules that allow writes to documents/{your user id}/_scan/ (from the project root: firebase deploy --only storage). ' +
      'If you recently subscribed, stay online briefly so your account syncs, then try again. ' +
      'You can still upload and enter details manually.'
    );
  }
  return msg;
};

// FileSystem.EncodingType can be undefined on web; use string fallback so read/write still work.
const BASE64_ENCODING = (typeof FileSystem !== 'undefined' && FileSystem.EncodingType?.Base64 != null)
  ? FileSystem.EncodingType.Base64
  : 'base64';
const SCAN_FALLBACK_DELAY_MS = 15000;
/** Resize before scan upload so typical phone photos (~3–12MP) don’t spend a long time in fetch/blob + Storage. */
const IMAGE_SCAN_OPTIMIZE_THRESHOLD_BYTES = 512 * 1024;
const IMAGE_SCAN_MAX_WIDTH = 2048;

type ScanPhase = 'idle' | 'preparing' | 'uploading' | 'processing';

const scanPhaseButtonLabel = (phase: ScanPhase): string => {
  switch (phase) {
    case 'preparing':
      return 'Preparing scan...';
    case 'uploading':
      return 'Uploading...';
    case 'processing':
      return 'Reading document...';
    default:
      return 'Scanning...';
  }
};

const guessMimeType = (fileName: string, fallback = 'application/octet-stream'): string => {
  const ext = getFileExtension(fileName);
  switch (ext) {
    case 'pdf':
      return 'application/pdf';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'heic':
      return 'image/heic';
    case 'heif':
      return 'image/heif';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    default:
      return fallback;
  }
};

const isAllowedFileType = (mimeType: string, fileName: string): boolean => {
  return hasAllowedMimeOrExtension(
    mimeType,
    fileName,
    DOCUMENT_ALLOWED_MIME_TYPES,
    DOCUMENT_ALLOWED_EXTENSIONS,
  );
};

const ensureUploadsDir = async () => {
  const uploadsDir = `${FileSystem.cacheDirectory}uploads/`;
  try {
    await FileSystem.makeDirectoryAsync(uploadsDir, { intermediates: true });
  } catch (error) {
    // Directory may already exist; that's fine.
  }
  return uploadsDir;
};

const cacheFileToUploads = async (uri: string, fileName: string, mimeType?: string): Promise<SelectedFile> => {
  // On web, FileSystem.copyAsync is unavailable. Use the picked file directly.
  if (Platform.OS === 'web') {
    const response = await fetch(uri);
    const blob = await response.blob();

    return {
      uri,
      name: fileName,
      type: mimeType || blob.type || guessMimeType(fileName),
      size: blob.size,
    };
  }

  const uploadsDir = await ensureUploadsDir();
  const sanitizedName = fileName.replace(/\s+/g, '_');
  const targetUri = `${uploadsDir}${Date.now()}_${sanitizedName}`;

  try {
    await FileSystem.copyAsync({ from: uri, to: targetUri });
  } catch (copyError) {
    // On iOS, copyAsync can fail even with full photo access (e.g. ph:// URIs). Fall back to
    // read-as-base64 then write (same idea as ReceiptUpload's convertUriToBlob path).
    console.warn('[DocumentUpload] copyAsync failed, trying read-then-write fallback:', copyError);
    const base64 = await FileSystem.readAsStringAsync(uri, {
      encoding: BASE64_ENCODING,
    });
    await FileSystem.writeAsStringAsync(targetUri, base64, {
      encoding: BASE64_ENCODING,
    });
  }

  const info = await FileSystem.getInfoAsync(targetUri);
  const byteSize =
    info.exists && typeof (info as { size?: number }).size === 'number'
      ? (info as { size: number }).size
      : 0;

  return {
    uri: targetUri,
    name: fileName,
    type: mimeType || guessMimeType(fileName),
    size: byteSize,
  };
};

/** Cache image from base64 (e.g. from ImagePicker with base64: true). Avoids reading asset URI after picker closes, which can fail on iOS with limited photo library. */
const cacheBase64ToUploads = async (
  base64: string,
  fileName: string,
  mimeType?: string
): Promise<SelectedFile> => {
  if (Platform.OS === 'web') {
    const dataUri = `data:${mimeType || 'image/jpeg'};base64,${base64}`;
    const response = await fetch(dataUri);
    const blob = await response.blob();
    return {
      uri: dataUri,
      name: fileName,
      type: mimeType || blob.type || guessMimeType(fileName),
      size: blob.size,
    };
  }
  const uploadsDir = await ensureUploadsDir();
  const sanitizedName = fileName.replace(/\s+/g, '_');
  const targetUri = `${uploadsDir}${Date.now()}_${sanitizedName}`;
  await FileSystem.writeAsStringAsync(targetUri, base64, {
    encoding: BASE64_ENCODING,
  });
  const info = await FileSystem.getInfoAsync(targetUri);
  const byteSize =
    info.exists && typeof (info as { size?: number }).size === 'number'
      ? (info as { size: number }).size
      : 0;
  return {
    uri: targetUri,
    name: fileName,
    type: mimeType || guessMimeType(fileName),
    size: byteSize,
  };
};

export function DocumentUploadModal({
  visible,
  onClose,
  onSuccess,
  initialEntryMode = 'upload',
  allowUpload = true,
}: DocumentUploadModalProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const { height: screenHeight } = Dimensions.get('window');
  const sheetMaxHeight = Math.min(screenHeight * 0.94, screenHeight - insets.top - 16);
  const sheetMinHeight = Math.max(screenHeight * 0.6, 440);
  const isSignedIn = Boolean(firebaseUser?.uid);
  
  const [entryMode, setEntryMode] = useState<EntryMode>('upload');
  const [documentName, setDocumentName] = useState('');
  const [documentType, setDocumentType] = useState<DocumentType>('other');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [metadata, setMetadata] = useState<Partial<DocumentMetadata>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [scanning, setScanning] = useState(false);
  const [scanPhase, setScanPhase] = useState<ScanPhase>('idle');
  const [scanJobId, setScanJobId] = useState<string | null>(null);
  const [ocrExtracted, setOcrExtracted] = useState<Partial<DocumentMetadata> | null>(null);
  const [scanReviewState, setScanReviewState] = useState<ScanReviewState | null>(null);
  const scanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scanFallbackRef = useRef<{ storagePath: string; documentType: DocumentType; contentType: string } | null>(null);
  const autoScanFileRef = useRef<string | null>(null);
  const scanPreparedPayloadRef = useRef<{ fileUri: string; payload: ScanUploadPayload } | null>(null);
  const scanSessionRef = useRef(0);
  const scanResolvedRef = useRef(false);
  const scanTimingRef = useRef<{
    startedAt: number;
    payloadPreparedAt?: number;
    uploadStartedAt?: number;
    uploadCompletedAt?: number;
    fallbackStartedAt?: number;
  } | null>(null);
  const [showFileInfoSheet, setShowFileInfoSheet] = useState(false);

  const parseDateMaybe = (value: unknown): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
    if (typeof value === 'string') {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? undefined : d;
    }
    return undefined;
  };

  // OCR returns ISO strings for dates; convert them to Date objects so form fields bind correctly.
  const normalizeOcrMetadata = (raw: Record<string, unknown>): Partial<DocumentMetadata> => {
    const out: Record<string, unknown> = { ...raw };
    const dateFields = [
      'issueDate',
      'expirationDate',
      'entryDate',
      'exitDate',
      'date',
      'coverageStartDate',
      'coverageEndDate',
    ];
    for (const f of dateFields) {
      const parsed = parseDateMaybe(out[f]);
      if (parsed) out[f] = parsed;
    }
    return out as Partial<DocumentMetadata>;
  };

  const computeExpiresOnFromMetadata = (
    type: DocumentType,
    meta: Partial<DocumentMetadata> | null | undefined
  ): Date | undefined => {
    const m: any = meta || {};
    if (type === 'insurance') {
      const d = parseDateMaybe(m.coverageEndDate);
      if (d) return d;
    }
    const exp = parseDateMaybe(m.expirationDate);
    return exp;
  };

  const getRequiredMetadataFields = (type: DocumentType): string[] => {
    switch (type) {
      case 'passport':
      case 'visa':
      case 'driving_license':
        return ['number', 'issuingCountry', 'expirationDate'];
      case 'insurance':
        return ['policyNumber', 'provider', 'coverageStartDate'];
      case 'health_record':
        return ['recordType', 'providerName', 'date'];
      default:
        return [];
    }
  };

  const validateSelectedFile = (file: SelectedFile): boolean => {
    if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
      Alert.alert(
        'File too large',
        `Please select a file under ${formatFileSize(MAX_DOCUMENT_SIZE_BYTES)}. Current size: ${formatFileSize(file.size)}`
      );
      return false;
    }

    if (!isAllowedFileType(file.type, file.name)) {
      Alert.alert(
        'Unsupported file type',
        'Allowed types: PDF, JPG, PNG, DOC, DOCX.'
      );
      return false;
    }

    return true;
  };

  const fileSupportsScanning = (file: SelectedFile | null): boolean => {
    if (!file) return false;
    const contentType = file.type || guessMimeType(file.name);
    const isPdf = contentType === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isImage =
      contentType.startsWith('image/') ||
      /\.(png|jpg|jpeg|webp)$/i.test(file.name);
    return isPdf || isImage;
  };

  const clearScanTimeout = () => {
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
  };

  const clearPreparedScanPayload = (fileUri?: string) => {
    if (!scanPreparedPayloadRef.current) return;
    if (!fileUri || scanPreparedPayloadRef.current.fileUri === fileUri) {
      scanPreparedPayloadRef.current = null;
    }
  };

  const finishScanSession = (sessionId: number, source: string): boolean => {
    if (scanSessionRef.current !== sessionId || scanResolvedRef.current) {
      return false;
    }
    scanResolvedRef.current = true;
    clearScanTimeout();
    scanFallbackRef.current = null;
    setScanning(false);
    setScanPhase('idle');
    setScanJobId(null);
    const timing = scanTimingRef.current;
    if (timing) {
      logger.info('[DocumentUploadModal] Scan timing complete', {
        source,
        totalMs: Date.now() - timing.startedAt,
        payloadPrepMs: timing.payloadPreparedAt ? timing.payloadPreparedAt - timing.startedAt : undefined,
        uploadMs:
          timing.uploadStartedAt && timing.uploadCompletedAt
            ? timing.uploadCompletedAt - timing.uploadStartedAt
            : undefined,
        fallbackDelayMs:
          timing.fallbackStartedAt ? timing.fallbackStartedAt - timing.startedAt : undefined,
      });
      scanTimingRef.current = null;
    }
    return true;
  };

  const prepareScanUploadPayload = async (
    file: SelectedFile,
    requestedContentType: string,
  ): Promise<ScanUploadPayload> => {
    const cachedPayload = scanPreparedPayloadRef.current;
    if (cachedPayload && cachedPayload.fileUri === file.uri && cachedPayload.payload.contentType === requestedContentType) {
      logger.info('[DocumentUploadModal] Reusing cached scan payload', {
        fileName: file.name,
        contentType: requestedContentType,
        sizeBytes: cachedPayload.payload.fileBlob.size,
        optimized: cachedPayload.payload.optimized,
      });
      return cachedPayload.payload;
    }

    let sourceUri = file.uri;
    let contentType = requestedContentType;
    let optimized = false;

    const shouldOptimizeImage =
      Platform.OS !== 'web' &&
      requestedContentType.startsWith('image/') &&
      file.size >= IMAGE_SCAN_OPTIMIZE_THRESHOLD_BYTES;

    if (shouldOptimizeImage) {
      try {
        const manipulated = await ImageManipulator.manipulateAsync(
          file.uri,
          [{ resize: { width: IMAGE_SCAN_MAX_WIDTH } }],
          { compress: 0.82, format: ImageManipulator.SaveFormat.JPEG },
        );
        sourceUri = manipulated.uri;
        contentType = 'image/jpeg';
        optimized = true;
      } catch (error) {
        logger.warn(
          '[DocumentUploadModal] Image optimization skipped',
          undefined,
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    }

    const response = await fetch(sourceUri);
    const fileBlob = await response.blob();
    const payload: ScanUploadPayload = {
      fileBlob,
      contentType: contentType || fileBlob.type || requestedContentType,
      optimized,
      sourceUri,
      preparedAtMs: Date.now(),
    };
    scanPreparedPayloadRef.current = { fileUri: file.uri, payload };
    logger.info('[DocumentUploadModal] Scan payload prepared', {
      fileName: file.name,
      requestedContentType,
      payloadContentType: payload.contentType,
      payloadSizeBytes: payload.fileBlob.size,
      optimized,
    });
    return payload;
  };

  const resetForm = (mode: EntryMode = initialEntryMode) => {
    const nextMode: EntryMode = allowUpload ? mode : 'manual';
    setEntryMode(nextMode);
    setDocumentName('');
    setDocumentType('other');
    setSelectedFile(null);
    setMetadata({}); // Initialize as empty object instead of null
    setErrors({});
    setUploading(false);
    setScanning(false);
    setScanPhase('idle');
    setScanJobId(null);
    setOcrExtracted(null);
    setScanReviewState(null);
    autoScanFileRef.current = null;
    clearPreparedScanPayload();
    clearScanTimeout();
    scanFallbackRef.current = null;
    scanResolvedRef.current = false;
    scanTimingRef.current = null;
    setShowFileInfoSheet(false);
  };

  const handleClose = () => {
    if (!uploading) {
      resetForm(initialEntryMode);
      onClose();
    }
  };

  // When opening the modal, ensure we start in the requested mode.
  // This is what powers "Upload" vs "Manual entry" buttons on the Documents screen.
  useEffect(() => {
    if (!visible) return;
    if (uploading || scanning) return;
    setEntryMode(allowUpload ? initialEntryMode : 'manual');
    // Intentionally do not reset other fields here; those are handled by resetForm on close/success.
  }, [visible, initialEntryMode, allowUpload, uploading, scanning]);

  const ensureSignedIn = (): boolean => {
    if (!isSignedIn) {
      Alert.alert('Sign in required', 'Please sign in to upload or scan documents.');
      return false;
    }
    return true;
  };

  // Subscribe to background scan result (expense-style flow)
  useEffect(() => {
    const uid = firebaseUser?.uid;
    if (!uid || !scanJobId) return;
    const sessionId = scanSessionRef.current;

    const jobRef = doc(db, 'users', uid, 'documentScanJobs', scanJobId);
    const unsub = onSnapshot(
      jobRef,
      (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data();
      const status = data?.status as string | undefined;

      if (status === 'parsed' || status === 'needs_review') {
        if (!finishScanSession(sessionId, 'snapshot')) return;
        const metadata = data?.metadata as Record<string, unknown> | undefined;
        const documentNameResult = data?.documentName as string | undefined;
        const detectedType = data?.detectedDocumentType as string | undefined;
        const parseWarnings = Array.isArray(data?.parseWarnings) ? data.parseWarnings.filter((v: unknown) => typeof v === 'string') : [];
        const fieldConfidence = data?.fieldConfidence && typeof data.fieldConfidence === 'object'
          ? (data.fieldConfidence as Record<string, number>)
          : {};
        const missingRequiredFields = Array.isArray(data?.missingRequiredFields)
          ? data.missingRequiredFields.filter((v: unknown) => typeof v === 'string')
          : [];
        const rawText = typeof data?.rawText === 'string' ? data.rawText : undefined;
        const ocrEngine = typeof data?.ocrEngine === 'string' ? data.ocrEngine : undefined;
        const extractionSource = typeof data?.extractionSource === 'string' ? data.extractionSource : undefined;

        setScanReviewState({
          parseStatus: status as ScanReviewState['parseStatus'],
          parseWarnings,
          fieldConfidence,
          missingRequiredFields,
          rawText,
          detectedDocumentType: detectedType,
          ocrEngine,
          extractionSource,
        });

        if (metadata && Object.keys(metadata).length > 0) {
          const normalized = normalizeOcrMetadata(metadata);
          setOcrExtracted(normalized);
          setMetadata((prev) => ({ ...prev, ...normalized }));
          if (
            documentType === 'other' &&
            detectedType &&
            ['passport', 'visa', 'driving_license', 'health_record', 'insurance', 'other'].includes(detectedType)
          ) {
            setDocumentType(detectedType as DocumentType);
          }
          if (documentNameResult && !documentName.trim()) {
            setDocumentName(documentNameResult);
          }
          const alertFn = (title: string, message: string) => {
            if (Platform.OS === 'web' && typeof window !== 'undefined') {
              window.alert(`${title}\n\n${message}`);
            } else {
              Alert.alert(title, message);
            }
          };
          alertFn(
            status === 'parsed' ? 'Scan Complete' : 'Review Required',
            status === 'parsed'
              ? 'Document data extracted successfully. Review the details below.'
              : 'We extracted some document data, but a few fields still need review.'
          );
        } else {
          const alertFn = (title: string, message: string) => {
            if (Platform.OS === 'web' && typeof window !== 'undefined') {
              window.alert(`${title}\n\n${message}`);
            } else {
              Alert.alert(title, message);
            }
          };
          setScanReviewState((prev) => ({
            ...prev,
            parseStatus: 'failed',
            parseWarnings: [...(prev?.parseWarnings || []), 'No structured fields were extracted.'],
          }));
          alertFn('No Data Found', 'Could not extract data from this document. Please enter details manually.');
        }
        unsub();
        return;
      }

      if (status === 'failed') {
        if (!finishScanSession(sessionId, 'snapshot_failed')) return;
        setScanReviewState({
          parseStatus: 'failed',
          parseWarnings: ['Document scan failed.'],
          missingRequiredFields: [],
        });
        const errMsg = (data?.error as string) || 'Unknown error';
        const alertFn = (title: string, message: string) => {
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.alert(`${title}\n\n${message}`);
          } else {
            Alert.alert(title, message);
          }
        };
        alertFn('Scan Failed', `Could not scan document (${errMsg}). You can still upload and enter details manually.`);
        unsub();
      }
      },
      (error) => {
        logger.warn(
          '[DocumentUploadModal] scan job subscription error',
          undefined,
          error instanceof Error ? error : new Error(String(error)),
        );
        if (!finishScanSession(sessionId, 'snapshot_error')) return;
        setScanReviewState({
          parseStatus: 'failed',
          parseWarnings: ['Document scan connection lost.'],
          missingRequiredFields: [],
        });
        const alertFn = (title: string, message: string) => {
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.alert(`${title}\n\n${message}`);
          } else {
            Alert.alert(title, message);
          }
        };
        alertFn(
          'Scan connection lost',
          'We lost connection to the document scanner. You can re-try the scan or enter details manually.',
        );
      },
    );

    return () => {
      unsub();
      clearScanTimeout();
    };
  }, [firebaseUser?.uid, scanJobId, documentType, documentName]);

  useEffect(() => {
    if (!visible || entryMode !== 'upload' || !selectedFile || scanning || uploading) return;
    if (ocrExtracted || scanReviewState?.parseStatus) return;
    if (!fileSupportsScanning(selectedFile)) return;
    if (!networkMonitor.isOnline()) return;
    if (autoScanFileRef.current === selectedFile.uri) return;
    autoScanFileRef.current = selectedFile.uri;
    void handleScanDocument();
  }, [visible, entryMode, selectedFile?.uri, scanning, uploading, ocrExtracted, scanReviewState?.parseStatus, documentType]);

  // Validation functions
  const validateManualEntry = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!documentName.trim()) {
      newErrors.name = 'Document name is required';
    }

    // Metadata is always an object now, so we can proceed with validation

    switch (documentType) {
      case 'passport': {
        const passportMeta = metadata as Partial<import('@/services/documentVault').PassportMetadata>;
        if (!passportMeta?.number?.trim()) {
          newErrors.number = 'Passport number is required';
        }
        if (!passportMeta?.issuingCountry) {
          newErrors.issuingCountry = 'Issuing country is required';
        }
        if (!passportMeta?.expirationDate) {
          newErrors.expirationDate = 'Expiration date is required';
        }
        break;
      }
      case 'driving_license': {
        const licenseMeta = metadata as Partial<import('@/services/documentVault').DrivingLicenseMetadata>;
        if (!licenseMeta?.number?.trim()) {
          newErrors.number = 'License number is required';
        }
        if (!licenseMeta?.issuingCountry) {
          newErrors.issuingCountry = 'Issuing country is required';
        }
        if (!licenseMeta?.expirationDate) {
          newErrors.expirationDate = 'Expiration date is required';
        }
        break;
      }
      case 'visa': {
        const visaMeta = metadata as Partial<import('@/services/documentVault').VisaMetadata>;
        if (!visaMeta?.number?.trim()) {
          newErrors.number = 'Visa number is required';
        }
        if (!visaMeta?.issuingCountry) {
          newErrors.issuingCountry = 'Issuing country is required';
        }
        if (!visaMeta?.expirationDate) {
          newErrors.expirationDate = 'Expiration date is required';
        }
        break;
      }
      case 'health_record': {
        const healthMeta = metadata as Partial<import('@/services/documentVault').HealthRecordMetadata>;
        if (!healthMeta?.recordType) {
          newErrors.recordType = 'Record type is required';
        }
        if (!healthMeta?.providerName?.trim()) {
          newErrors.providerName = 'Provider name is required';
        }
        if (!healthMeta?.date) {
          newErrors.date = 'Date is required';
        }
        break;
      }
      case 'insurance': {
        const insuranceMeta = metadata as Partial<import('@/services/documentVault').InsuranceMetadata>;
        if (!insuranceMeta?.policyNumber?.trim()) {
          newErrors.policyNumber = 'Policy number is required';
        }
        if (!insuranceMeta?.provider?.trim()) {
          newErrors.provider = 'Provider is required';
        }
        if (!insuranceMeta?.coverageStartDate) {
          newErrors.coverageStartDate = 'Coverage start date is required';
        }
        break;
      }
      case 'other': {
        const otherMeta = metadata as Partial<import('@/services/documentVault').OtherDocumentMetadata>;
        if (!otherMeta?.documentNumber?.trim()) {
          newErrors.documentNumber = 'Document number is required';
        }
        if (!otherMeta?.issuingAuthority?.trim()) {
          newErrors.issuingAuthority = 'Issuing authority is required';
        }
        break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateUploadEntry = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedFile) {
      newErrors.file = 'Please select a file to upload';
    }
    if (!documentName.trim()) {
      newErrors.name = 'Document name is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return false;
    }

    if (selectedFile && !validateSelectedFile(selectedFile)) {
      return false;
    }

    return true;
  };

  const requestCameraPermission = async () => {
    if (Platform.OS !== 'web') {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          'Permission needed',
          'Camera permission is required to take photos. You can also use Browse files without camera access.',
          [
            { text: 'Not now', style: 'cancel' },
            { text: 'Open Settings', onPress: () => { void Linking.openSettings(); } },
          ],
        );
        return false;
      }
    }
    return true;
  };

  const requestMediaLibraryPermission = async () => {
    if (Platform.OS !== 'web') {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          'Permission needed',
          'We need access to your photo library to upload documents. You can continue with Browse files instead, or enable access in Settings.',
          [
            { text: 'Use Browse files', style: 'cancel' },
            { text: 'Open Settings', onPress: () => { void Linking.openSettings(); } },
          ],
        );
        return false;
      }
    }
    return true;
  };

  const handleTakePhoto = async () => {
    if (!ensureSignedIn()) return;
    const hasPermission = await requestCameraPermission();
    if (!hasPermission) return;

    try {
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        const asset = result.assets[0];
        const fileName = `photo_${Date.now()}.jpg`;
        const mimeType = asset.mimeType || 'image/jpeg';
        const cachedFile = await cacheFileToUploads(asset.uri, fileName, mimeType);
        const file: SelectedFile = {
          ...cachedFile,
          type: mimeType,
          size: cachedFile.size || asset.fileSize || 0,
        };

        if (!validateSelectedFile(file)) {
          await FileSystem.deleteAsync(file.uri, { idempotent: true });
          return;
        }

        clearPreparedScanPayload(selectedFile?.uri);
        setSelectedFile(file);
        setOcrExtracted(null);
        setScanReviewState(null);
        autoScanFileRef.current = null;

        // Auto-fill document name if empty
        if (!documentName) {
          setDocumentName(`Photo ${new Date().toLocaleDateString()}`);
        }
      }
    } catch (error) {
      console.error('Error taking photo:', error);
      Alert.alert('Error', 'Failed to take photo. Please try again.');
    }
  };

  const handlePickPhoto = async () => {
    if (!ensureSignedIn()) return;
    const hasPermission = await requestMediaLibraryPermission();
    if (!hasPermission) return;

    try {
      // Use base64 when possible so we don't read the asset URI after the picker closes (avoids
      // "failed to access photo library" on iOS with limited library or ph:// URIs).
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: false,
        quality: 0.8,
        base64: true,
      });

      const asset = result.canceled ? null : result.assets?.[0];
      if (!asset?.uri) {
        return;
      }

      const mimeType = asset.mimeType || 'image/jpeg';

      const ext =
        mimeType.includes('png')
          ? 'png'
          : mimeType.includes('heic')
            ? 'heic'
            : mimeType.includes('heif')
              ? 'heif'
              : 'jpg';
      const fileName = asset.fileName || `photo_${Date.now()}.${ext}`;

      const base64Data = typeof asset.base64 === 'string' && asset.base64.length > 0 ? asset.base64 : null;
      let cachedFile: SelectedFile;
      if (base64Data) {
        cachedFile = await cacheBase64ToUploads(base64Data, fileName, mimeType);
      } else {
        cachedFile = await cacheFileToUploads(asset.uri, fileName, mimeType);
      }
      const file: SelectedFile = {
        ...cachedFile,
        type: mimeType,
        size: cachedFile.size || asset.fileSize || 0,
      };

      if (!validateSelectedFile(file)) {
        await FileSystem.deleteAsync(file.uri, { idempotent: true });
        return;
      }

      clearPreparedScanPayload(selectedFile?.uri);
      setSelectedFile(file);
      setOcrExtracted(null);
      setScanReviewState(null);
      autoScanFileRef.current = null;

      // Auto-fill document name if empty
      if (!documentName) {
        const nameWithoutExt = fileName.split('.').slice(0, -1).join('.');
        setDocumentName(nameWithoutExt || fileName);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[DocumentUpload] Error picking photo:', message, error);
      const shortMessage = message.length > 100 ? `${message.slice(0, 97)}…` : message;
      Alert.alert(
        'Couldn’t use selected photo',
        `Something went wrong: ${shortMessage}\n\nTry "Browse files" to pick an image instead.`,
        [{ text: 'OK' }],
      );
    }
  };

  const handlePickDocument = async () => {
    if (!ensureSignedIn()) return;
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/pdf',
          'image/*',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ],
        copyToCacheDirectory: true,
        multiple: false,
      });

      if (result.canceled || !result.assets?.[0]) {
        return;
      }

      const asset = result.assets[0];
      const fileName = asset.name || `document_${Date.now()}`;
      const mimeType = asset.mimeType || guessMimeType(fileName);

      if (!isAllowedFileType(mimeType, fileName)) {
        Alert.alert('Unsupported file type', 'Allowed types: PDF, JPG, PNG, DOC, DOCX.');
        return;
      }

      const cachedFile = await cacheFileToUploads(asset.uri, fileName, mimeType);
      const size = cachedFile.size || asset.size || 0;
      const file: SelectedFile = {
        ...cachedFile,
        type: mimeType,
        size,
      };

      if (!validateSelectedFile(file)) {
        await FileSystem.deleteAsync(file.uri, { idempotent: true });
        return;
      }

      clearPreparedScanPayload(selectedFile?.uri);
      setSelectedFile(file);
      setOcrExtracted(null);
      setScanReviewState(null);
      autoScanFileRef.current = null;

      // Auto-fill document name if empty
      if (!documentName) {
        const nameWithoutExt = fileName.split('.').slice(0, -1).join('.');
        setDocumentName(nameWithoutExt || fileName);
      }
    } catch (error) {
      console.error('Error picking document:', error);
      Alert.alert('Error', 'Failed to pick document. Please try again.');
    }
  };

  const handleScanDocument = async () => {
    if (!ensureSignedIn()) return;
    const uid = firebaseUser?.uid;
    if (!uid) return;

    const alertFn = (title: string, message: string) => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.alert(`${title}\n\n${message}`);
      } else {
        Alert.alert(title, message);
      }
    };

    if (!selectedFile) {
      alertFn('No file selected', 'Please select a file first to scan.');
      return;
    }

    if (!networkMonitor.isOnline()) {
      alertFn('Offline', 'Scanning requires an internet connection.');
      return;
    }

    const contentType = selectedFile.type || guessMimeType(selectedFile.name);

    if (!fileSupportsScanning(selectedFile)) {
      alertFn('Scan not supported', 'Scanning currently supports PDF, JPG, and PNG files. You can still upload this file and enter details manually.');
      return;
    }

    const sessionId = scanSessionRef.current + 1;
    scanSessionRef.current = sessionId;
    scanResolvedRef.current = false;
    scanTimingRef.current = { startedAt: Date.now() };
    setScanning(true);
    setScanPhase('preparing');
    setOcrExtracted(null);
    setScanReviewState(null);

    try {
      const payload = await prepareScanUploadPayload(selectedFile, contentType);
      if (scanTimingRef.current) {
        scanTimingRef.current.payloadPreparedAt = payload.preparedAtMs;
      }
      if (scanSessionRef.current !== sessionId || scanResolvedRef.current) {
        return;
      }
      setScanPhase('uploading');
      if (scanTimingRef.current) {
        scanTimingRef.current.uploadStartedAt = Date.now();
      }
      const { jobId, storagePath } = await startDocumentScan(
        uid,
        payload.fileBlob,
        selectedFile.name,
        documentType,
        payload.contentType,
      );
      if (scanTimingRef.current) {
        scanTimingRef.current.uploadCompletedAt = Date.now();
      }
      setScanPhase('processing');
      logger.info('[DocumentUploadModal] Background scan job created', {
        jobId,
        documentType,
        contentType: payload.contentType,
        optimizedPayload: payload.optimized,
      });
      scanFallbackRef.current = { storagePath, documentType, contentType: payload.contentType };
      setScanJobId(jobId);
      // If background job doesn't complete promptly, fall back to callable.
      clearScanTimeout();
      scanTimeoutRef.current = setTimeout(async () => {
        scanTimeoutRef.current = null;
        if (scanSessionRef.current !== sessionId || scanResolvedRef.current) return;
        const fallback = scanFallbackRef.current;
        if (!fallback) return;
        if (scanTimingRef.current) {
          scanTimingRef.current.fallbackStartedAt = Date.now();
        }
        logger.info('[DocumentUploadModal] Scan fallback triggered', {
          delayMs: SCAN_FALLBACK_DELAY_MS,
          jobId,
          documentType: fallback.documentType,
        });
        try {
          const ocrResult = await extractDocumentFields({
            storagePath: fallback.storagePath,
            documentType: fallback.documentType,
            contentType: fallback.contentType,
          });
          if (!finishScanSession(sessionId, 'fallback')) return;
          if (ocrResult.metadata && Object.keys(ocrResult.metadata).length > 0) {
            const normalized = normalizeOcrMetadata(ocrResult.metadata as Record<string, unknown>);
            setOcrExtracted(normalized);
            setMetadata((prev) => ({ ...prev, ...normalized }));
            setScanReviewState({
              parseStatus: ocrResult.parseStatus,
              parseWarnings: ocrResult.parseWarnings || [],
              fieldConfidence: ocrResult.fieldConfidence || {},
              missingRequiredFields: ocrResult.missingRequiredFields || [],
              rawText: ocrResult.rawText,
              detectedDocumentType: typeof ocrResult.detectedDocumentType === 'string' ? ocrResult.detectedDocumentType : undefined,
              ocrEngine: ocrResult.ocrEngine,
              extractionSource: ocrResult.extractionSource,
            });
            if (
              fallback.documentType === 'other' &&
              ocrResult.detectedDocumentType &&
              ['passport', 'visa', 'driving_license', 'health_record', 'insurance', 'other'].includes(String(ocrResult.detectedDocumentType))
            ) {
              setDocumentType(ocrResult.detectedDocumentType as DocumentType);
            }
            if (ocrResult.documentName && !documentName.trim()) setDocumentName(String(ocrResult.documentName));
            alertFn(
              ocrResult.parseStatus === 'parsed' ? 'Scan Complete' : 'Review Required',
              ocrResult.parseStatus === 'parsed'
                ? 'Document data extracted. Review the details below.'
                : 'We extracted some data, but a few fields still need review.'
            );
          } else {
            setScanReviewState({
              parseStatus: 'failed',
              parseWarnings: ocrResult.parseWarnings || ['No structured fields were extracted.'],
              fieldConfidence: {},
              missingRequiredFields: ocrResult.missingRequiredFields || [],
              rawText: ocrResult.rawText,
              detectedDocumentType: typeof ocrResult.detectedDocumentType === 'string' ? ocrResult.detectedDocumentType : undefined,
              ocrEngine: ocrResult.ocrEngine,
              extractionSource: ocrResult.extractionSource,
            });
            alertFn('No Data Found', 'Could not extract data from this document. Please enter details manually.');
          }
        } catch (err: any) {
          if (!finishScanSession(sessionId, 'fallback_error')) return;
          setScanReviewState({
            parseStatus: 'failed',
            parseWarnings: [String(err?.message || err || 'Document scan failed.')],
            missingRequiredFields: [],
          });
          alertFn('Scan Failed', (err?.message || String(err)) + '. You can still enter details manually.');
        }
      }, SCAN_FALLBACK_DELAY_MS);
    } catch (error: unknown) {
      console.error('[DocumentUploadModal] Scan start error:', error);
      finishScanSession(sessionId, 'start_error');
      const message = humanizeScanStartError(error);
      alertFn('Scan Failed', message);
    }
  };

  const handleSubmit = async () => {
    if (!ensureSignedIn()) return;
    if (entryMode === 'manual') {
      const isValid = validateManualEntry();
      if (!isValid) {
        // Use a callback to get the latest errors after state update
        requestAnimationFrame(() => {
          const errorMessages = Object.values(errors);
          if (errorMessages.length > 0) {
            Alert.alert('Validation Error', 'Please fix the following:\n• ' + errorMessages.join('\n• '));
          } else {
            Alert.alert('Validation Error', 'Please fill in all required fields.');
          }
        });
        return;
      }
    } else {
      if (!validateUploadEntry()) {
        Alert.alert('Validation Error', 'Please select a file and enter a document name.');
        return;
      }
    }

    let finalMetadata: DocumentMetadata | undefined = undefined;
    let expiresOn: Date | undefined = undefined;

    finalMetadata = metadata && typeof metadata === 'object' && Object.keys(metadata).length > 0 ? (metadata as DocumentMetadata) : undefined;
    expiresOn = computeExpiresOnFromMetadata(documentType, finalMetadata);

    setUploading(true);

    try {
      const metadataToSave =
        metadata && Object.keys(metadata).length > 0
          ? (metadata as DocumentMetadata)
          : finalMetadata || undefined;
      const currentMissingRequiredFields = getRequiredMetadataFields(documentType).filter((field) => {
        const value = (metadataToSave as Record<string, unknown> | undefined)?.[field];
        return value == null || value === '';
      });
      const parseStatusToSave =
        entryMode === 'manual'
          ? 'manual'
          : currentMissingRequiredFields.length > 0
            ? scanReviewState?.rawText
              ? 'needs_review'
              : 'failed'
            : scanReviewState?.rawText
              ? 'parsed'
              : 'manual';

      if (entryMode === 'manual' && networkMonitor.shouldUseOfflineDataFallback()) {
        await queueManualDocumentSave({
          name: documentName.trim(),
          type: documentType,
          expiresOn: expiresOn ? expiresOn.toISOString() : undefined,
          metadata: metadataToSave,
          parseStatus: parseStatusToSave,
          ocrRawText: scanReviewState?.rawText || null,
          fieldConfidence: scanReviewState?.fieldConfidence || {},
          parseWarnings: scanReviewState?.parseWarnings || [],
          missingRequiredFields: currentMissingRequiredFields,
          detectedDocumentType: scanReviewState?.detectedDocumentType || null,
          ocrEngine: scanReviewState?.ocrEngine || null,
          extractionSource:
            scanReviewState?.extractionSource || (entryMode === 'manual' ? 'manual' : null),
        });

        Alert.alert(
          'Queued for upload',
          'You are offline. We will save your document automatically once you are back online.'
        );
        resetForm(initialEntryMode);
        onSuccess();
        onClose();
        return;
      }

      let storagePath: string | null = null;
      let uploadContentType: string | undefined = undefined;

      if (entryMode === 'upload') {
        if (!selectedFile) {
          throw new Error('File missing. Please re-select your file.');
        }

        const contentType = selectedFile.type || guessMimeType(selectedFile.name);
        uploadContentType = contentType;

        if (networkMonitor.shouldUseOfflineDataFallback()) {
          const durableUri = await copyUriToPersistentPendingUpload(selectedFile.uri, selectedFile.name);
          await queueDocumentUpload({
            fileUri: durableUri,
            fileName: selectedFile.name,
            documentName: documentName.trim(),
            contentType,
            documentType,
            expiresOn: expiresOn ? expiresOn.toISOString() : undefined,
            metadata: metadataToSave,
          });

          Alert.alert(
            'Queued for upload',
            'You are offline. We will upload your document automatically once you are back online.'
          );
          resetForm(initialEntryMode);
          onSuccess();
          onClose();
          return;
        }

        // Step 1: Prepare upload (get storage path)
        const { storagePath: path } = await prepareDocumentUpload(
          selectedFile.name,
          contentType
        );

        // Step 2: Upload file to Firebase Storage using client SDK (handles auth automatically)
        let fileData: Blob;
        
        const response = await fetch(selectedFile.uri);
        fileData = await response.blob();

        // Use Firebase client SDK for upload (works in both emulator and production)
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, fileData, {
          contentType,
          customMetadata: firebaseUser?.uid ? { uploadedBy: firebaseUser.uid } : undefined,
        });

        // Small delay to ensure file is fully written (especially important for emulator)
        await new Promise(resolve => setTimeout(resolve, 500));

        storagePath = path;
      }

      // Step 3: Finalize (save to Firestore)
      await finalizeDocumentUpload({
        name: documentName.trim(),
        type: documentType,
        entryType: entryMode,
        storagePath,
        expiresOn,
        metadata: metadataToSave,
        encryptionMethod: 'none',
        parseStatus: parseStatusToSave,
        ocrRawText: scanReviewState?.rawText || null,
        fieldConfidence: scanReviewState?.fieldConfidence || {},
        parseWarnings: scanReviewState?.parseWarnings || [],
        missingRequiredFields: currentMissingRequiredFields,
        detectedDocumentType: scanReviewState?.detectedDocumentType || null,
        ocrEngine: scanReviewState?.ocrEngine || null,
        extractionSource: scanReviewState?.extractionSource || (entryMode === 'manual' ? 'manual' : null),
      });

      Alert.alert('Success', entryMode === 'upload' ? 'Document uploaded successfully!' : 'Document saved successfully!');
      resetForm(initialEntryMode);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving document:', error);
      Alert.alert(
        'Error',
        error instanceof Error ? error.message : 'Failed to save document. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  const canSubmit = useMemo(() => {
    if (entryMode === 'upload') {
      return selectedFile !== null && documentName.trim().length > 0;
    } else {
      // Basic check - full validation happens on submit
      // Allow submit if document name is filled (metadata can be empty object initially)
      return documentName.trim().length > 0;
    }
  }, [entryMode, selectedFile, documentName]);

  const extractedFieldCount = useMemo(
    () => Object.keys(metadata || {}).filter((key) => metadata?.[key as keyof typeof metadata] != null && metadata?.[key as keyof typeof metadata] !== '').length,
    [metadata]
  );
  const currentMissingRequiredFields = useMemo(
    () => getRequiredMetadataFields(documentType).filter((field) => {
      const value = (metadata as Record<string, unknown> | undefined)?.[field];
      return value == null || value === '';
    }),
    [documentType, metadata]
  );

  const selectedFileIsImage =
    Boolean(selectedFile?.type?.startsWith('image/')) ||
    Boolean(selectedFile?.name && /\.(png|jpe?g|webp|heic|heif)$/i.test(selectedFile.name));

  return (
    <>
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={handleClose}
      statusBarTranslucent
      presentationStyle={Platform.OS === 'ios' ? 'fullScreen' : 'overFullScreen'}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.surface,
          justifyContent: 'flex-start',
        }}
      >
        <SelectFieldModalProvider>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'height' : undefined}
          keyboardVerticalOffset={0}
          style={{ flex: 1 }}
        >
          <View
            style={{
              flex: 1,
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              overflow: 'hidden',
            }}
          >
            {/* Grabber / Handle (common top-app bottom sheet pattern) */}
            <View style={{ alignItems: 'center', paddingTop: theme.spacing(1), paddingBottom: theme.spacing(0.5) }}>
              <View
                style={{
                  width: 44,
                  height: 5,
                  borderRadius: 999,
                  backgroundColor: theme.colors.borderMuted,
                  opacity: 0.8,
                }}
              />
            </View>

            <ModalHeader
              title={entryMode === 'upload' ? 'Upload Document' : 'Enter Document'}
              onClose={handleClose}
              leftActionLabel="Cancel"
              onLeftAction={handleClose}
              disableClose={uploading}
              disableLeftAction={uploading}
            />

            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{
                padding: theme.spacing(2),
                gap: theme.spacing(2),
                // Keep content comfortably above the sticky action bar.
                paddingBottom: insets.bottom + theme.spacing(12),
              }}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
              automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
            >
              {/* Entry Mode Toggle */}
              {allowUpload && (
                <Card variant="outlined">
                  <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                    <Pressable
                      onPress={() => {
                        setEntryMode('upload');
                        setMetadata({});
                        setErrors({});
                        setScanReviewState(null);
                      }}
                      disabled={uploading}
                      style={({ pressed }) => [
                        {
                          flex: 1,
                          paddingVertical: theme.spacing(1.5),
                          paddingHorizontal: theme.spacing(2),
                          borderRadius: theme.radii.md,
                          backgroundColor: entryMode === 'upload' ? theme.colors.primary : theme.colors.background,
                          alignItems: 'center',
                          opacity: pressed ? 0.85 : 1,
                        },
                      ]}
                    >
                      <ThemedText
                        variant="bodyStrong"
                        color={entryMode === 'upload' ? theme.colors.surface : theme.colors.text}
                      >
                        Upload File
                      </ThemedText>
                    </Pressable>
                    <Pressable
                      onPress={() => {
                        setEntryMode('manual');
                        setSelectedFile(null);
                        setErrors({});
                        setOcrExtracted(null);
                        setScanReviewState(null);
                      }}
                      disabled={uploading}
                      style={({ pressed }) => [
                        {
                          flex: 1,
                          paddingVertical: theme.spacing(1.5),
                          paddingHorizontal: theme.spacing(2),
                          borderRadius: theme.radii.md,
                          backgroundColor: entryMode === 'manual' ? theme.colors.primary : theme.colors.background,
                          alignItems: 'center',
                          opacity: pressed ? 0.85 : 1,
                        },
                      ]}
                    >
                      <ThemedText
                        variant="bodyStrong"
                        color={entryMode === 'manual' ? theme.colors.surface : theme.colors.text}
                      >
                        Enter Manually
                      </ThemedText>
                    </Pressable>
                  </View>
                </Card>
              )}

              {entryMode === 'upload' ? (
                <>
                  <Card variant="outlined">
                    <SubHeading>Select File</SubHeading>
                    <View style={{ gap: theme.spacing(1.5) }}>
                      {!isSignedIn ? (
                        <Caption color={theme.colors.textSecondary}>
                          Sign in to upload or scan documents.
                        </Caption>
                      ) : null}
                      <Button
                        label="Take Photo"
                        onPress={handleTakePhoto}
                        icon={<Ionicons name="camera-outline" size={20} color={theme.colors.surface} />}
                        disabled={uploading || !isSignedIn}
                      />
                      <Button
                        label="Choose from Photos"
                        variant="secondary"
                        onPress={handlePickPhoto}
                        icon={<Ionicons name="images-outline" size={20} color={theme.colors.primary} />}
                        disabled={uploading || !isSignedIn}
                      />
                      <Button
                        label="Browse files"
                        variant="secondary"
                        onPress={handlePickDocument}
                        icon={<Ionicons name="document-outline" size={20} color={theme.colors.primary} />}
                        disabled={uploading || !isSignedIn}
                      />
                      {selectedFile && (
                        <View style={{ gap: theme.spacing(1) }}>
                          <View
                            style={{
                              padding: theme.spacing(1.5),
                              backgroundColor: theme.colors.background,
                              borderRadius: theme.radii.md,
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: theme.spacing(1),
                            }}
                          >
                            <Pressable
                              style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}
                              onPress={() => setShowFileInfoSheet(true)}
                              disabled={uploading || scanning}
                              accessibilityRole="button"
                              accessibilityLabel={`File ${selectedFile.name}, view details`}
                            >
                              <Ionicons name="document-attach-outline" size={20} color={theme.colors.primary} />
                              <View style={{ flex: 1 }}>
                                <ThemedText variant="bodyStrong">{selectedFile.name}</ThemedText>
                                <Caption color={theme.colors.textSecondary}>{selectedFile.type}</Caption>
                                <Caption color={theme.colors.textSecondary} style={{ marginTop: 2, opacity: 0.85 }}>
                                  Tap for file info{selectedFile.type.startsWith('image/') ? ' and preview' : ''}
                                </Caption>
                              </View>
                            </Pressable>
                            <Pressable
                              onPress={() => {
                                clearPreparedScanPayload(selectedFile?.uri);
                                setSelectedFile(null);
                                setOcrExtracted(null);
                                setScanReviewState(null);
                                autoScanFileRef.current = null;
                                setShowFileInfoSheet(false);
                              }}
                              disabled={uploading || scanning}
                              accessibilityRole="button"
                              accessibilityLabel="Remove file"
                            >
                              <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
                            </Pressable>
                          </View>
                          <Button
                            label={
                              scanning
                                ? scanPhaseButtonLabel(scanPhase)
                                : ocrExtracted
                                  ? 'Re-scan Document'
                                  : 'Scan Document'
                            }
                            variant="secondary"
                            onPress={handleScanDocument}
                            disabled={uploading || scanning}
                            icon={scanning ? <ActivityIndicator size="small" color={theme.colors.primary} /> : <Ionicons name="scan-outline" size={18} color={theme.colors.primary} />}
                          />
                          {scanning ? (
                            <Caption color={theme.colors.textSecondary} style={{ marginTop: 2 }}>
                              Secure upload and text extraction often take 20–60 seconds, depending on your connection
                              {scanPhase === 'processing' ? ' and server load' : ''}.
                            </Caption>
                          ) : null}
                        </View>
                      )}
                      {(scanReviewState || ocrExtracted) && (
                        <View
                          style={{
                            padding: theme.spacing(1.5),
                            backgroundColor:
                              scanReviewState?.parseStatus === 'failed'
                                ? theme.colors.error + '10'
                                : scanReviewState?.parseStatus === 'needs_review'
                                  ? theme.colors.warning + '10'
                                  : (theme.colors.successContainer || theme.colors.primary + '10'),
                            borderRadius: theme.radii.md,
                            borderLeftWidth: 3,
                            borderLeftColor:
                              scanReviewState?.parseStatus === 'failed'
                                ? theme.colors.error
                                : scanReviewState?.parseStatus === 'needs_review'
                                  ? theme.colors.warning
                                  : (theme.colors.success || '#22C55E'),
                            gap: theme.spacing(0.75),
                            marginBottom: theme.spacing(1),
                          }}
                        >
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                            <Ionicons
                              name={
                                scanReviewState?.parseStatus === 'failed'
                                  ? 'alert-circle'
                                  : scanReviewState?.parseStatus === 'needs_review'
                                    ? 'warning'
                                    : 'checkmark-circle'
                              }
                              size={16}
                              color={
                                scanReviewState?.parseStatus === 'failed'
                                  ? theme.colors.error
                                  : scanReviewState?.parseStatus === 'needs_review'
                                    ? theme.colors.warning
                                    : (theme.colors.success || '#22C55E')
                              }
                            />
                            <Caption
                              color={
                                scanReviewState?.parseStatus === 'failed'
                                  ? theme.colors.error
                                  : scanReviewState?.parseStatus === 'needs_review'
                                    ? theme.colors.warning
                                    : (theme.colors.success || '#22C55E')
                              }
                              style={{ fontWeight: '600' }}
                            >
                              {scanReviewState?.parseStatus === 'failed'
                                ? 'Scan failed. Manual review required.'
                                : scanReviewState?.parseStatus === 'needs_review'
                                  ? 'Partial extraction. Review highlighted fields below.'
                                  : 'Data extracted. Review and edit below.'}
                            </Caption>
                          </View>
                          <Caption color={theme.colors.textSecondary}>
                            {extractedFieldCount} field{extractedFieldCount === 1 ? '' : 's'} populated
                            {currentMissingRequiredFields.length
                              ? `, ${currentMissingRequiredFields.length} required field${currentMissingRequiredFields.length === 1 ? '' : 's'} still missing`
                              : ''}
                            {scanReviewState?.ocrEngine ? `, source: ${scanReviewState.ocrEngine.replace(/_/g, ' ')}` : ''}
                          </Caption>
                          {scanReviewState?.parseWarnings?.slice(0, 2).map((warning) => (
                            <Caption key={warning} color={theme.colors.textSecondary}>
                              {warning}
                            </Caption>
                          ))}
                          {scanReviewState?.rawText ? (
                            <Caption numberOfLines={4} color={theme.colors.textSecondary}>
                              OCR preview: {scanReviewState.rawText.replace(/\s+/g, ' ').trim()}
                            </Caption>
                          ) : null}
                        </View>
                      )}
                      {errors.file && <Caption color={theme.colors.error}>{errors.file}</Caption>}
                    </View>
                  </Card>

                  <InputField
                    label="Document Name"
                    placeholder="Enter document name"
                    value={documentName}
                    onChangeText={setDocumentName}
                    errorText={errors.name}
                    editable={!uploading}
                  />

                  <SelectField
                    label="Document Type"
                    value={documentType}
                    options={DOCUMENT_TYPE_OPTIONS}
                    onValueChange={(value) => {
                      setDocumentType(value as DocumentType);
                      // Preserve existing metadata when switching document type
                    }}
                  />

                  <DocumentFormFields
                    documentType={documentType}
                    metadata={metadata}
                    onMetadataChange={setMetadata}
                    errors={errors}
                    disabled={uploading}
                  />
                </>
              ) : (
                <>
                  <InputField
                    label="Document Name"
                    placeholder="Enter document name"
                    value={documentName}
                    onChangeText={setDocumentName}
                    errorText={errors.name}
                    editable={!uploading}
                  />

                  <SelectField
                    label="Document Type"
                    value={documentType}
                    options={DOCUMENT_TYPE_OPTIONS}
                    onValueChange={(value) => {
                      setDocumentType(value as DocumentType);
                      setMetadata({}); // Initialize as empty object instead of null
                      setErrors({});
                    }}
                  />

                  <DocumentFormFields
                    documentType={documentType}
                    metadata={metadata}
                    onMetadataChange={setMetadata}
                    errors={errors}
                    disabled={uploading}
                  />
                </>
              )}
            </ScrollView>

            {/* Sticky action bar (single primary action like top apps) */}
            <View
              style={[
                styles.actionBar,
                {
                  backgroundColor: Platform.OS === 'ios' ? 'transparent' : theme.colors.surface,
                  borderTopColor: theme.colors.borderMuted,
                  paddingBottom: theme.spacing(2) + insets.bottom,
                  paddingTop: theme.spacing(1.25),
                  paddingHorizontal: theme.spacing(2),
                },
              ]}
            >
              {Platform.OS === 'ios' ? (
                <BlurView
                  intensity={55}
                  tint={theme.colorScheme === 'dark' ? 'dark' : 'light'}
                  style={StyleSheet.absoluteFill}
                />
              ) : (
                <View style={[StyleSheet.absoluteFill, { backgroundColor: theme.colors.surface }]} />
              )}
              <Button
                label={
                  uploading
                    ? entryMode === 'upload'
                      ? 'Uploading...'
                      : 'Saving...'
                    : entryMode === 'upload'
                      ? 'Upload Document'
                      : 'Save Document'
                }
                onPress={handleSubmit}
                disabled={!canSubmit || uploading || scanning}
                size="large"
                icon={
                  uploading ? (
                    <ActivityIndicator size="small" color={theme.colors.surface} />
                  ) : (
                    <Ionicons
                      name={entryMode === 'upload' ? 'cloud-upload-outline' : 'save-outline'}
                      size={20}
                      color={theme.colors.surface}
                    />
                  )
                }
              />
            </View>
          </View>
        </KeyboardAvoidingView>
        </SelectFieldModalProvider>
      </View>
    </Modal>

    <Modal
      visible={showFileInfoSheet && Boolean(selectedFile)}
      animationType="fade"
      transparent
      onRequestClose={() => setShowFileInfoSheet(false)}
      presentationStyle="overFullScreen"
      statusBarTranslucent
    >
      <View style={{ flex: 1, backgroundColor: theme.colors.overlay, justifyContent: 'flex-end' }}>
        <Pressable style={{ flex: 1 }} onPress={() => setShowFileInfoSheet(false)} accessibilityLabel="Dismiss" />
        <View
          style={{
            backgroundColor: theme.colors.surface,
            borderTopLeftRadius: theme.radii.xl,
            borderTopRightRadius: theme.radii.xl,
            padding: theme.spacing(2),
            paddingBottom: insets.bottom + theme.spacing(2),
            gap: theme.spacing(2),
            maxHeight: '85%',
          }}
        >
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <SubHeading>Selected file</SubHeading>
            <Pressable onPress={() => setShowFileInfoSheet(false)} accessibilityRole="button" accessibilityLabel="Close">
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          </View>
          {selectedFile ? (
            <ScrollView contentContainerStyle={{ gap: theme.spacing(2) }}>
              <View style={{ gap: theme.spacing(1) }}>
                <Caption color={theme.colors.textSecondary}>Name</Caption>
                <ThemedText variant="body">{selectedFile.name}</ThemedText>
              </View>
              <View style={{ gap: theme.spacing(1) }}>
                <Caption color={theme.colors.textSecondary}>Type</Caption>
                <ThemedText variant="body">{selectedFile.type}</ThemedText>
              </View>
              <View style={{ gap: theme.spacing(1) }}>
                <Caption color={theme.colors.textSecondary}>Size</Caption>
                <ThemedText variant="body">
                  {selectedFile.size > 0
                    ? selectedFile.size < 1024
                      ? `${selectedFile.size} B`
                      : selectedFile.size < 1024 * 1024
                        ? `${(selectedFile.size / 1024).toFixed(1)} KB`
                        : `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB`
                    : 'Unknown'}
                </ThemedText>
              </View>
              {selectedFileIsImage ? (
                <View style={{ gap: theme.spacing(1) }}>
                  <Caption color={theme.colors.textSecondary}>Preview</Caption>
                  <View
                    style={{
                      borderRadius: theme.radii.md,
                      overflow: 'hidden',
                      borderWidth: 1,
                      borderColor: theme.colors.borderMuted,
                      backgroundColor: theme.colors.background,
                      maxHeight: 280,
                    }}
                  >
                    <Image source={{ uri: selectedFile.uri }} style={{ width: '100%', height: 240 }} resizeMode="contain" />
                  </View>
                </View>
              ) : null}
            </ScrollView>
          ) : null}
          <Button label="Done" variant="secondary" onPress={() => setShowFileInfoSheet(false)} />
        </View>
      </View>
    </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  actionBar: {
    borderTopWidth: StyleSheet.hairlineWidth,
    // Subtle separation (iOS/Android)
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: -3 },
    elevation: 6,
  },
});
